import { useCallback, useState } from 'react';
import { ImageFormat } from 'corgi-types';
import type { ArticlesSortingScore } from 'graphql-ts-types';
import { PlpBackend, SearchBackend } from 'graphql-ts-types';

import { GrowthbookFlag } from 'common/constants/feature-flags';
import { executeQuery } from 'common/gql';
import type { ProductListQuery, SearchProductListQuery } from 'common/gql/generated/schema';
import { toGQLUrlParams } from 'common/gql/utils/url';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import type { QueryHashRef } from 'common/hooks/gql';
import { useGqlQueryHashRef } from 'common/hooks/gql';
import { useGQLLocale } from 'common/hooks/locale';
import type { Translate } from 'common/providers/LocaleProvider';
import { useLocalization } from 'common/providers/LocaleProvider';
import { transformArticleTrackingData } from 'common/tracking/transformArticlesTrackingData';
import { extractCampaignBadge } from 'common/utils/extract-campaign-badge';
import isWebpSupported from 'common/utils/isWebpSupported';
import type { ProductListsParams, QueryArguments } from '../components/types';
import { useGetFetchProductsQuery } from './use-get-fetch-products-query';

interface ExecuteFetchDataArgs {
  queryHashRef: QueryHashRef;
  variables: QueryArguments;
  isSearchMode: boolean;
  t: Translate;
  withCampaignBadge?: boolean;
}

interface UseFetchProductProps {
  id?: string;
  query?: string;
  pageSize: number;
  isSearchMode: boolean;
  sortingScore?: ArticlesSortingScore;
  params?: ProductListsParams;
  setTotalCount: (totalCount: number) => void;
  withCampaignBadge?: boolean;
}

const SWIPE_PLP_GALLERY_IMAGE_LIMIT = 20;

const fetchAndTransformData = async ({
  queryHashRef,
  variables,
  isSearchMode,
  t,
  withCampaignBadge,
}: ExecuteFetchDataArgs) => {
  const { categories } = await executeQuery<
    typeof isSearchMode extends true ? SearchProductListQuery : ProductListQuery,
    QueryArguments
  >({ queryHashRef, variables });

  const categoryList = isSearchMode ? categories : categories && categories[0]?.categoryArticles;

  if (!categoryList || !('articles' in categoryList)) {
    throw new Error('Categories query failed to return articles');
  }

  const { articles, totalCount } = categoryList;

  return {
    loadedArticles: articles
      .filter((a): a is NonNullable<typeof a> => a !== null)
      .map(article => ({
        ...transformArticleTrackingData(article, t),
        ...(withCampaignBadge && {
          campaignBadge: extractCampaignBadge(article.campaigns),
        }),
      })),
    totalCount,
  };
};

export const useFetchProducts = ({
  id,
  query,
  pageSize,
  isSearchMode,
  sortingScore,
  params = {
    backend: isSearchMode ? SearchBackend.ThirdParty : PlpBackend.Home24,
  },
  setTotalCount,
  withCampaignBadge,
}: UseFetchProductProps) => {
  const locale = useGQLLocale();
  const { t } = useLocalization();
  const gqlQuery = useGetFetchProductsQuery(isSearchMode);
  const queryHashRef = useGqlQueryHashRef(gqlQuery);
  const [isLoading, setIsLoading] = useState(false);
  const withSwipePLPGallery = useFeatureFlagIsOn(GrowthbookFlag.SwipePLPGallery);
  const fetchProducts = useCallback(
    async (calculatedOffset: number, first = pageSize) => {
      setIsLoading(true);
      try {
        const variables = {
          urlParams: toGQLUrlParams(window.location.search),
          locale,
          first,
          offset: calculatedOffset,
          ...(isSearchMode ? { query } : { id }),
          ...(isWebpSupported() && { articleImageFormat: ImageFormat.WEBP }),
          ...(sortingScore && { sortingScore }),
          ...(withSwipePLPGallery && { plpImageLimit: SWIPE_PLP_GALLERY_IMAGE_LIMIT }),
          ...params,
        } as QueryArguments;
        const { loadedArticles, totalCount } = await fetchAndTransformData({
          queryHashRef,
          variables,
          isSearchMode,
          t,
          withCampaignBadge,
        });

        setTotalCount(totalCount);

        return loadedArticles;
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize, locale, isSearchMode, id, query, sortingScore, withCampaignBadge]
  );

  return { fetchProducts, isLoading };
};
